import { useEffect, useRef, useState, useCallback } from 'react'
import type { Signal, WebSocketMessage } from '@/types'

const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
const WS_URL = import.meta.env.VITE_WS_URL || `${WS_PROTOCOL}//${window.location.host}/ws/signals`

interface UseWebSocketOptions {
  symbol?: string
  onSignalNew?: (signal: Signal) => void
  onSignalDisappeared?: (signalId: string) => void
  reconnectInterval?: number
}

interface UseWebSocketReturn {
  isConnected: boolean
  lastMessage: WebSocketMessage | null
  connect: () => void
  disconnect: () => void
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    symbol,
    onSignalNew,
    onSignalDisappeared,
    reconnectInterval = 5000,
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const pingIntervalRef = useRef<number | null>(null)
  // Suppresses the reconnect-on-close behavior when disconnect() was called
  // intentionally (e.g. on unmount), since ws.close() fires onclose
  // asynchronously and would otherwise reopen a socket for a dead hook.
  const shouldReconnectRef = useRef(true)

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    const url = symbol ? `${WS_URL}?symbol=${symbol}` : WS_URL
    shouldReconnectRef.current = true

    try {
      const ws = new WebSocket(url)

      ws.onopen = () => {
        console.log('WebSocket connected')
        setIsConnected(true)

        // Start ping interval
        pingIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30000)
      }

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          setLastMessage(message)

          switch (message.type) {
            case 'signal_new':
              if (message.data && onSignalNew) {
                onSignalNew(message.data as unknown as Signal)
              }
              break
            case 'signal_disappeared':
              if (message.data?.id && onSignalDisappeared) {
                onSignalDisappeared(message.data.id as string)
              }
              break
            case 'pong':
              // Heartbeat received
              break
            case 'ping':
              // Server ping, respond with pong
              ws.send(JSON.stringify({ type: 'pong' }))
              break
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onclose = () => {
        console.log('WebSocket disconnected')
        setIsConnected(false)
        clearTimers()

        if (!shouldReconnectRef.current) {
          return
        }

        // Attempt to reconnect
        reconnectTimeoutRef.current = window.setTimeout(() => {
          console.log('Attempting to reconnect...')
          connect()
        }, reconnectInterval)
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      wsRef.current = ws
    } catch (error) {
      console.error('Failed to create WebSocket:', error)

      // Attempt to reconnect
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect()
      }, reconnectInterval)
    }
  }, [symbol, onSignalNew, onSignalDisappeared, reconnectInterval, clearTimers])

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false
    clearTimers()
    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
  }, [clearTimers])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect()
    return () => {
      disconnect()
    }
  }, [connect, disconnect])

  return {
    isConnected,
    lastMessage,
    connect,
    disconnect,
  }
}

export default useWebSocket
